import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { CORPORATE_SOLUTIONS, QUOTE_WA } from "../lib/constants";
import SectionHeading from "../components/SectionHeading";
import ServiceIcon from "../components/ServiceIcon";

const HERO_IMG =
  "https://images.pexels.com/photos/6050125/pexels-photo-6050125.jpeg";

export default function CorporateSolutions() {
  return (
    <div data-testid="corporate-solutions-page">
      <section className="relative h-[64vh] min-h-[460px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Executive in airport" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#05070C] via-[#05070C]/80 to-[#05070C]/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#05070C]/30 via-transparent to-[#05070C]" />
        </div>
        <div className="relative container-x pb-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <div className="eyebrow mb-6">
              <span className="hairline" /> Corporate Suite
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05]">
              Travel programmes built for <span className="gold-text italic">growing enterprises.</span>
            </h1>
            <p className="mt-6 max-w-xl text-white/65">
              Policy, spend, traveller wellbeing and reporting — under one
              senior consultant who knows your business by name.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section">
        <div className="container-x">
          <SectionHeading
            eyebrow="What you get"
            title="A control-room for everything that moves."
            goldWord="moves."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 mt-16 border border-white/5">
            {CORPORATE_SOLUTIONS.map((c, i) => (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                data-testid={`corp-solution-${i}`}
                className="bg-[#05070C] p-9 hover:bg-[#0B1120] transition-colors duration-500 group"
              >
                <span className="grid place-items-center h-12 w-12 rounded-full border border-gold/30 text-gold mb-7 group-hover:border-gold transition-colors">
                  <ServiceIcon name={c.icon} className="h-5 w-5" />
                </span>
                <h3 className="font-display text-2xl text-white mb-3">{c.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{c.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section bg-[#0B1120]/40 border-y border-white/5">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="How we onboard"
              title="From kickoff to first booking in 14 days."
              goldWord="14 days."
            />
            <p className="mt-6 text-white/60">
              A proven onboarding sprint, run by your account director with a
              senior consultant pair. Quietly fast.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-px bg-white/5 border border-white/5">
            {[
              { step: "01", title: "Discovery & travel audit", body: "Analyse 12 months of historic spend, traveller patterns and policy gaps." },
              { step: "02", title: "Policy & supplier design", body: "Codify policy, negotiate rates, integrate preferred suppliers." },
              { step: "03", title: "Tooling & training", body: "Configure dashboards, approval flows and traveller profiles. Train stakeholders." },
              { step: "04", title: "Live operations", body: "Dedicated account manager, 24/7 support, monthly executive reviews." },
            ].map((p) => (
              <div
                key={p.step}
                className="bg-[#05070C] p-8 grid grid-cols-12 gap-6 hover:bg-[#0B1120] transition-colors"
                data-testid={`onboard-step-${p.step}`}
              >
                <div className="col-span-2 text-gold font-display text-3xl">{p.step}</div>
                <div className="col-span-10">
                  <h4 className="font-display text-xl text-white mb-2">{p.title}</h4>
                  <p className="text-sm text-white/55 leading-relaxed">{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Promise list */}
      <section className="section">
        <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <div className="relative aspect-[5/4] overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1767694942371-3a9e2d155b04?w=1600&q=80"
                alt="Private jet"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#05070C]/80 via-transparent to-transparent" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <SectionHeading
              eyebrow="Our promise"
              title="Never the cheapest. Always the most considered."
              goldWord="considered."
            />
            <ul className="mt-10 space-y-5">
              {[
                "Senior consultants only — no juniors on your account",
                "Average response time under 8 minutes, 24/7",
                "Live policy compliance dashboard — no surprises",
                "Quarterly travel reviews with your executive team",
                "Sustainability reporting and CO₂ optimisation",
              ].map((p) => (
                <li key={p} className="flex items-start gap-4 text-white/80">
                  <Check className="h-5 w-5 text-gold mt-0.5 shrink-0" strokeWidth={2} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <a
              href={QUOTE_WA}
              target="_blank"
              rel="noreferrer"
              data-testid="corp-page-cta"
              className="btn-gold mt-12"
            >
              Request proposal on WhatsApp
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
